import { useCallback, useMemo } from 'react';

import { ConsumerStudentFee } from '@givebacks/api/student';
import { ClickableOptionsCard } from 'core/Components';
import { DateTime } from 'luxon';
import { Divider, Stack, Text, TextProps, ThemeIcon, centsToDollar } from 'ui';
import { IconCircleCheck, IconPartiallyPaid } from 'ui/Icons';

type StudentPaymentCardProps = {
  payment: ConsumerStudentFee;
  onClick: (feeId: string) => void;
};

interface StudentPaymentCardIconProps {
  isPartiallyPaid: boolean;
}

interface PaymentTextProps extends TextProps {
  children: React.ReactNode;
}

interface PaymentPaidDateProps {
  isPartiallyPaid: boolean;
  payment: ConsumerStudentFee;
}

interface PaymentRightSectionProps {
  payment: ConsumerStudentFee;
  isPartiallyPaid: boolean;
}

const PaymentText = ({ children, ...props }: PaymentTextProps) => {
  return (
    <Text size="sm" c="gray.6" {...props}>
      {children}
    </Text>
  );
};

const PaymentRightSection = ({ payment, isPartiallyPaid }: PaymentRightSectionProps) => {
  return (
    <Stack flex={0} align="flex-end" justify="flex-start" gap="4px">
      <Text size="sm" c="gray.8" fw={600} style={{ whiteSpace: 'nowrap' }}>
        - {centsToDollar(payment.amount_paid)}
      </Text>
      <PaymentText style={{ whiteSpace: 'nowrap' }}>
        {isPartiallyPaid ? 'Partially Paid' : 'Fully Paid'}
      </PaymentText>
    </Stack>
  );
};

const PaymentPaidDate = ({ isPartiallyPaid, payment }: PaymentPaidDateProps) => {
  const lastPaymentDate = useMemo(() => {
    if (payment.last_payment_at) {
      const lastPaymentDate = DateTime.fromISO(payment.last_payment_at);
      if (lastPaymentDate.isValid) {
        return lastPaymentDate.toFormat('MM/dd/yyyy');
      }
    }
    return undefined;
  }, [payment.last_payment_at]);

  const paymentText = useMemo(() => {
    if (isPartiallyPaid) {
      return 'Last Paid: ';
    }
    return 'Paid: ';
  }, [isPartiallyPaid]);

  return (
    <PaymentText>
      {paymentText} {lastPaymentDate}
    </PaymentText>
  );
};

const PaymentCardIcon = ({ isPartiallyPaid }: StudentPaymentCardIconProps) => {
  return (
    <ThemeIcon size="64px" color="gray.1" c="mint.7" radius="sm">
      {isPartiallyPaid ? <IconPartiallyPaid size={32} /> : <IconCircleCheck size={32} />}
    </ThemeIcon>
  );
};

const StudentPaymentCard = ({ payment, onClick }: StudentPaymentCardProps) => {
  const isPartiallyPaid = useMemo(() => {
    return payment.amount_remaining > 0;
  }, [payment.amount_remaining]);

  const handleClick = useCallback(() => {
    onClick(payment.uuid);
  }, [payment.uuid, onClick]);

  return (
    <ClickableOptionsCard.Row
      leftSection={<PaymentCardIcon isPartiallyPaid={isPartiallyPaid} />}
      title={payment.fee.name}
      subtitle={<PaymentPaidDate isPartiallyPaid={isPartiallyPaid} payment={payment} />}
      onClick={handleClick}
      rightSection={<PaymentRightSection payment={payment} isPartiallyPaid={isPartiallyPaid} />}
    >
      <Divider size="xs" color="cobalt.0" />
    </ClickableOptionsCard.Row>
  );
};

export { StudentPaymentCard };
